/**
 * BlueBean CSV のアップロード(OPレポート・対応履歴をまとめて)。
 * 複数ファイルを一度に選ぶ → 種類・対象日を自動判定 → 日ごとに取込後の件数と状態(新規／上書き／差し替え／取込済み)を確認 → まとめて取込。
 * 変換の定義は docs/specs/bluebean-import.md。計画と実行は bulkImport.ts。
 */
import { useEffect, useMemo, useState, type ChangeEvent, type DragEvent } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import Modal from '@core/components/Modal';
import { useToast } from '@core/components/Toast';
import { weekdayLabel } from '@core/lib/date';
import type { Company, DateStr, Operator } from '@core/lib/types';
import { fetchOperators } from '@core/masters/operatorRepo';
import { FILE_STATUS_LABELS, executePlan, loadDateContext, loadFile, planUploads, type DateContext, type LoadedFile, type RunResult } from './bulkImport';
import type { UnknownBbIdPolicy } from './types';

const KIND_LABELS = { op_report: 'OPレポート', activity: '対応履歴', unknown: '不明' } as const;

export default function CsvUploadModal({ defaultDate, companies, onClose, onImported }: { defaultDate: DateStr; companies: Company[]; onClose: () => void; onImported: (dates: DateStr[]) => void }) {
  const { user } = useAuth();
  const toast = useToast();
  const [files, setFiles] = useState<LoadedFile[]>([]);
  const [ctx, setCtx] = useState<Map<DateStr, DateContext>>(new Map());
  const [operators, setOperators] = useState<Operator[] | null>(null);
  const [policy, setPolicy] = useState<UnknownBbIdPolicy>('abort');
  const [includeOverwrite, setIncludeOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<Map<string, RunResult>>(new Map());
  const [finished, setFinished] = useState(false);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    void fetchOperators()
      .then(setOperators)
      .catch(() => toast('オペレーターマスタの読み込みに失敗しました'));
  }, [toast]);

  // ファイルの対象日ぶんの取込状況(既存の日次・bb_daily_stats・取込履歴)を読む
  const dates = useMemo(() => Array.from(new Set(files.map((f) => f.date))).sort(), [files]);
  useEffect(() => {
    let alive = true;
    const missing = dates.filter((d) => !ctx.has(d));
    if (missing.length === 0) return;
    void Promise.all(missing.map((d) => loadDateContext(d).then((c) => [d, c] as const)))
      .then((list) => {
        if (!alive) return;
        setCtx((prev) => {
          const next = new Map(prev);
          for (const [d, c] of list) next.set(d, c);
          return next;
        });
      })
      .catch(() => alive && toast('取込状況を読み込めませんでした'));
    return () => {
      alive = false;
    };
  }, [dates, ctx, toast]);

  async function addFiles(picked: File[]) {
    const csvs = picked.filter((f) => /\.csv$/i.test(f.name) || f.type === 'text/csv');
    if (csvs.length === 0) {
      if (picked.length > 0) toast('CSVファイルだけ取り込めます');
      return;
    }
    setBusy(true);
    try {
      const loaded = (await Promise.all(csvs.map((f) => loadFile(f, defaultDate)))).flat();
      setFiles((prev) => [...prev, ...loaded]);
      setFinished(false);
      setResults(new Map());
    } catch (err) {
      toast(err instanceof Error ? err.message : 'CSVを読み込めませんでした');
    } finally {
      setBusy(false);
    }
  }
  function handleFiles(e: ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = '';
    void addFiles(picked);
  }
  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    if (busy) return;
    void addFiles(Array.from(e.dataTransfer.files ?? []));
  }

  const setFileDate = (id: string, date: DateStr) => setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, date } : f)));
  const removeFile = (id: string) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const ready = files.length > 0 && !!operators && dates.every((d) => ctx.has(d));
  const plans = useMemo(() => (ready ? planUploads(files, ctx, { companies, operators: operators ?? [], policy }) : []), [ready, files, ctx, companies, operators, policy]);
  const planned = useMemo(() => plans.flatMap((p) => p.files), [plans]);
  const statusOf = (id: string) => planned.find((p) => p.file.id === id);
  const allIssues = useMemo(
    () => [
      ...planned.flatMap((p) => p.issues.map((i) => ({ ...i, message: `${p.file.name}: ${i.message}` }))),
      ...plans.flatMap((p) => p.buildIssues.map((i) => ({ ...i, message: `${p.date.replaceAll('-', '/')}: ${i.message}` }))),
    ],
    [planned, plans],
  );
  const hasError = allIssues.some((i) => i.level === 'error');
  const needsOverwrite = planned.some((p) => p.status === 'overwrite' || p.status === 'replace');
  const importable = planned.filter((p) => p.status === 'new' || ((p.status === 'overwrite' || p.status === 'replace') && includeOverwrite)).length;

  async function run() {
    if (!user || !ready || hasError || importable === 0) return;
    setBusy(true);
    setResults(new Map());
    try {
      const rs = await executePlan(plans, {
        operators: operators ?? [],
        companies,
        policy,
        userId: user.id,
        includeOverwrite,
        onProgress: (r) => setResults((prev) => new Map(prev).set(r.fileId, r)),
      });
      const done = rs.filter((r) => r.result === 'done').length;
      const failed = rs.filter((r) => r.result === 'error').length;
      toast(failed > 0 ? `${done}件を取り込み、${failed}件が失敗しました` : `${done}件を取り込みました`);
      setFinished(true);
      const doneDates = Array.from(new Set(plans.filter((p) => p.files.some((f) => rs.find((r) => r.fileId === f.file.id)?.result === 'done')).map((p) => p.date)));
      if (doneDates.length > 0) onImported(doneDates);
      // 取込状況を読み直す(続けて別のファイルを入れられるように)
      setCtx(new Map());
    } catch (err) {
      toast(err instanceof Error ? err.message : '取込に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const inputStyle = { height: 28, padding: '0 6px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit', fontSize: 12 } as const;
  const statusColor = (s: string) => (s === 'error' ? 'var(--red)' : s === 'overwrite' || s === 'replace' ? 'var(--amber)' : s === 'skip_same' ? 'var(--muted)' : 'inherit');

  return (
    <Modal onClose={onClose} width="min(900px, 94vw)">
      <h3>CSVアップロード(OPレポート・対応履歴)</h3>
      <p className="hint" style={{ margin: '4px 0 12px' }}>
        BlueBean の OPレポート(agent_report_…csv)と対応履歴(report_detail_…csv。リアクション情報=アポ〇)を、複数ファイルまとめて選べます。種類と対象日はファイルから判定します(対象日は表で直せます)。対応履歴は履歴登録日レンジを複数日(例: 9/1〜9/17)にして出力したファイルでもよく、日付ごとに分けて取り込みます。同じ日は OPレポート → 対応履歴 の順に取り込みます。
      </p>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!dragging) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        style={{
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 12,
          padding: '12px 14px',
          border: `2px dashed ${dragging ? 'var(--brand-blue)' : 'var(--line)'}`,
          borderRadius: 10,
          background: dragging ? 'var(--blue-bg)' : 'transparent',
        }}
      >
        <label className="btn-line" style={{ padding: '6px 14px', cursor: 'pointer' }}>
          ファイルを選ぶ(複数可)
          <input type="file" accept=".csv,text/csv" multiple onChange={handleFiles} style={{ display: 'none' }} disabled={busy} />
        </label>
        <span className="hint">ここに CSV をまとめてドロップしても取り込めます</span>
        {files.length > 0 && <span className="hint">／ {files.length}ファイル・対象日 {dates.map((d) => d.replaceAll('-', '/')).join('、')}</span>}
      </div>

      {files.length > 0 && (
        <div className="gray-box" style={{ marginTop: 0 }}>
          <div className="table-scroll">
            <table className="fit-table no-stack">
              <thead>
                <tr>
                  <th>ファイル</th>
                  <th>種類</th>
                  <th>対象日</th>
                  <th className="num">行数</th>
                  <th>内容</th>
                  <th>状態</th>
                  <th>結果</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {files.map((f) => {
                  const p = statusOf(f.id);
                  const r = results.get(f.id);
                  return (
                    <tr key={f.id}>
                      <td>{f.name}</td>
                      <td>{KIND_LABELS[f.kind]}</td>
                      <td>
                        <input type="date" value={f.date} onChange={(e) => e.target.value && setFileDate(f.id, e.target.value)} style={inputStyle} disabled={busy || finished} />
                        <span className="hint"> {weekdayLabel(f.date)}</span>
                      </td>
                      <td className="num">{f.rowCount}</td>
                      <td>{p?.summary ?? ''}</td>
                      <td style={{ color: statusColor(p?.status ?? '') }}>{p ? FILE_STATUS_LABELS[p.status] : ready ? '' : '確認中…'}</td>
                      <td style={{ color: r?.result === 'error' ? 'var(--red)' : r?.result === 'done' ? 'var(--green, #1a7f37)' : 'var(--muted)' }}>{r ? `${r.result === 'done' ? '取込済み' : r.result === 'error' ? '失敗' : 'スキップ'}: ${r.message}` : ''}</td>
                      <td>
                        {!busy && !finished && (
                          <button type="button" className="txn-link" onClick={() => removeFile(f.id)}>
                            外す
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {plans.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.7 }}>
              {plans.map((p) => {
                const c = ctx.get(p.date);
                const appts = p.build?.summary.staff.reduce((s, x) => s + x.appointments, 0) ?? 0;
                return (
                  <div key={p.date}>
                    <b>{p.date.replaceAll('-', '/')}</b>
                    <span className="hint">
                      {' '}
                      いま: OPレポート {c?.existing?.sources?.op_report ? 'あり' : 'なし'}
                      {(c?.existing?.sources?.activity ?? []).length > 0 ? `・対応履歴 ${(c?.existing?.sources?.activity ?? []).map((s) => `${s.business_codes.join('/')} ${s.appointments}件`).join('、')}` : '・対応履歴 なし'}
                    </span>
                    {p.build && (
                      <span>
                        {' '}
                        → 取込後: スタッフ <b>{p.build.summary.staff.length}</b>行・取得 <b>{appts}</b>件
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {allIssues.length > 0 && (
        <div className="gray-box">
          <div className="box-title">確認事項({allIssues.length}件)</div>
          <ul style={{ paddingLeft: 18, fontSize: 12.5, lineHeight: 1.7, margin: 0, maxHeight: 200, overflowY: 'auto' }}>
            {allIssues.map((i, n) => (
              <li key={n} style={{ color: i.level === 'error' ? 'var(--red)' : 'var(--amber)' }}>
                {i.row ? `${i.row}行目: ` : ''}
                {i.message}
              </li>
            ))}
          </ul>
          {allIssues.some((i) => i.code === 'unknown_bb_id') && (
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, fontSize: 13 }}>
              <input type="checkbox" checked={policy === 'exclude_rows'} onChange={(e) => setPolicy(e.target.checked ? 'exclude_rows' : 'abort')} />
              マスタに無いBB IDの行を除外して続行する(推奨: 先にオペレーターマスタへ登録)
            </label>
          )}
        </div>
      )}

      {needsOverwrite && !finished && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0', fontSize: 13 }}>
          <input type="checkbox" checked={includeOverwrite} onChange={(e) => setIncludeOverwrite(e.target.checked)} />
          既存の日次【集計】の上書き・同じ業務の対応履歴の差し替えも行う(前回分は1世代退避します)
        </label>
      )}

      <div className="modal-actions">
        {!finished ? (
          <button className="btn-green" disabled={busy || !ready || hasError || importable === 0} onClick={() => void run()}>
            {busy ? '取込中…' : `まとめて取り込む(${importable}件)`}
          </button>
        ) : (
          <button className="btn-green" onClick={onClose}>
            閉じる
          </button>
        )}
        <button className="btn-line" onClick={onClose} disabled={busy}>
          {finished ? '続けて取り込む場合はファイルを選ぶ' : 'キャンセル'}
        </button>
      </div>
    </Modal>
  );
}
